import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Send, Github, Linkedin, Mail, Phone, MapPin, CheckCircle, AlertCircle, X } from 'lucide-react'
import { CONTACT } from '../constants/testIds'
import { personalInfo } from '../data/portfolioData'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function Contact({ selectedService, onClearService }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [state, setState] = useState('idle')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (selectedService && selectedService !== 'general enquiry') {
      setForm(prev => ({
        ...prev,
        message: `Hi Akshay, I'm interested in your ${selectedService} service. Could you provide more details and a quote?`
      }))
    }
  }, [selectedService])

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return
    setState('loading')
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) { setState('success'); setForm({ name: '', email: '', message: '' }); onClearService?.() }
      else { const d = await res.json(); setErrorMsg(d.detail || 'Something went wrong.'); setState('error') }
    } catch {
      setErrorMsg('Network error. Please try again.')
      setState('error')
    }
  }

  return (
    <section id="contact" data-testid={CONTACT.section} className="py-16 md:py-24 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Contact</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.05 }}
          className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111] mb-10 md:mb-14">
          Let's build
          <br /><span className="text-[#6B7280]">something that lasts.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-14">

          {/* Left */}
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6">
            <p className="font-body text-[#6B7280] text-base md:text-lg leading-relaxed">
              Whether you're setting up a new store, need ongoing IT support, or want to build something custom — I'd love to hear about it.
            </p>
            <div className="space-y-3">
              <ContactDetail icon={Mail} label="Email" value={personalInfo.emailFallback} href={`mailto:${personalInfo.emailFallback}`} />
              <ContactDetail icon={Phone} label="Phone" value={personalInfo.phone} href={`tel:${personalInfo.phone.replace(/\s/g,'')}`} />
              <ContactDetail icon={MapPin} label="Location" value={personalInfo.location} />
            </div>
            <div className="flex items-center gap-3">
              <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub"
                className="p-3 border border-[#E5E7EB] rounded-xl bg-white hover:border-[#0047AB]/30 hover:bg-[#EBF2FF] transition-all duration-200 shadow-sm">
                <Github size={15} className="text-[#6B7280]" />
              </a>
              <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                className="p-3 border border-[#E5E7EB] rounded-xl bg-white hover:border-[#0047AB]/30 hover:bg-[#EBF2FF] transition-all duration-200 shadow-sm">
                <Linkedin size={15} className="text-[#6B7280]" />
              </a>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-emerald-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {personalInfo.availability}
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3">

            {/* Pre-fill service badge */}
            {selectedService && selectedService !== 'general enquiry' && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between p-3 bg-[#EBF2FF] border border-[#0047AB]/20 rounded-xl mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0047AB]" />
                  <span className="font-mono text-xs text-[#0047AB]">Enquiring about: <strong>{selectedService}</strong></span>
                </div>
                <button onClick={() => { onClearService?.(); setForm(prev => ({ ...prev, message: '' })) }}
                  aria-label="Clear service selection"
                  className="p-1 text-[#9CA3AF] hover:text-[#374151] transition-colors rounded">
                  <X size={12} />
                </button>
              </motion.div>
            )}

            {state === 'success' ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                data-testid={CONTACT.successMsg}
                className="flex flex-col items-center justify-center gap-4 p-12 border border-emerald-200 rounded-2xl bg-emerald-50 text-center">
                <CheckCircle size={36} className="text-emerald-600" />
                <p className="font-sans font-semibold text-[#111111] text-lg">Message received.</p>
                <p className="font-body text-[#6B7280] text-sm">I'll be in touch soon. Usually within 24 hours.</p>
                <button onClick={() => setState('idle')} className="mt-2 text-xs font-mono text-[#9CA3AF] hover:text-[#374151] underline">
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="Your name" testId={CONTACT.nameInput} required />
                  <FormField label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" testId={CONTACT.emailInput} required />
                </div>
                <FormField label="Message" name="message" type="textarea" value={form.message} onChange={handleChange}
                  placeholder="Tell me about your project or enquiry..." testId={CONTACT.messageInput} rows={5} required />
                {state === 'error' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    data-testid={CONTACT.errorMsg}
                    className="flex items-center gap-2 p-3 border border-red-200 rounded-xl bg-red-50 text-red-700 text-sm font-body">
                    <AlertCircle size={14} /> {errorMsg}
                  </motion.div>
                )}
                <button type="submit" disabled={state === 'loading'} data-testid={CONTACT.submitBtn}
                  className="w-full sm:w-auto px-8 py-3.5 bg-[#111111] text-white font-semibold text-sm rounded-xl hover:bg-[#0047AB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2">
                  {state === 'loading' ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
                  ) : (
                    <><Send size={14} />Send Message</>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

function ContactDetail({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-3 group">
      <div className="p-2.5 bg-[#EBF2FF] border border-[#0047AB]/15 rounded-xl group-hover:bg-[#0047AB] transition-colors duration-200">
        <Icon size={13} className="text-[#0047AB] group-hover:text-white transition-colors duration-200" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
        <div className="font-body text-[#374151] text-sm group-hover:text-[#111111] transition-colors duration-200">{value}</div>
      </div>
    </div>
  )
  return href ? <a href={href} className="block">{inner}</a> : <div>{inner}</div>
}

function FormField({ label, name, type, value, onChange, placeholder, testId, rows, required }) {
  const base = "w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#111111] text-sm font-body placeholder:text-[#D1D5DB] focus:outline-none focus:border-[#0047AB]/60 focus:ring-2 focus:ring-[#0047AB]/10 transition-all duration-200"
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">{label}</label>
      {type === 'textarea' ? (
        <textarea name={name} value={value} onChange={onChange} placeholder={placeholder}
          rows={rows || 4} required={required} data-testid={testId} className={`${base} resize-none`} />
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          required={required} data-testid={testId} className={base} />
      )}
    </div>
  )
}
